import React from 'react';
import SelectMui, { SelectProps as MuiSelectProps } from '@mui/material/Select';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import type { MenuProps as MuiMenuProps } from '@mui/material/Menu';
import InputLabel, { InputLabelProps } from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import HelperTextError from 'components/mui/Form/TextField/HelperTextError';
import elements from 'components/elements';

const ITEM_HEIGHT = 36;

const menuProps: Partial<MuiMenuProps> = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 8.5,
      },
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectBaseProps extends Omit<MuiSelectProps, 'label'> {
  options: SelectOption[];
  label: string;
  renderOptionLabel?: (option: SelectOption) => React.ReactNode;
  renderOptions?: () => React.ReactNode;
  id?: string;
  FormControlProps?: FormControlProps;
  InputLabelProps?: InputLabelProps;
  inputProps?: Record<string, any>;
  helperText?: string;
}

const SelectBase = React.forwardRef<any, SelectBaseProps>(
  (
    {
      label,
      options,
      renderOptionLabel,
      renderOptions,
      id,
      FormControlProps = {},
      InputLabelProps = {},
      inputProps = {},
      helperText,
      ...props
    },
    ref,
  ) => (
    <FormControl {...FormControlProps}>
      <InputLabel id={id} {...InputLabelProps}>
        {label}
      </InputLabel>
      <SelectMui
        ref={ref}
        labelId={id}
        label={label}
        MenuProps={menuProps}
        inputProps={{ ...inputProps, 'data-element': elements.popup.Upload.categorySelector.input }}
        {...props}
      >
        {typeof renderOptions === 'function'
          ? renderOptions()
          : options.map((o) => (
              <MenuItem
                key={`${o.value}-${o.label}`}
                value={o.value}
                disabled={o?.disabled || false}
                data-element={elements.popup.Upload.categorySelector.listItem}
              >
                {typeof renderOptionLabel === 'function' ? renderOptionLabel(o) : o.label}
              </MenuItem>
            ))}
      </SelectMui>
      {helperText && FormControlProps.error && (
        <FormHelperText>
          <HelperTextError isError={FormControlProps.error} meta={{ error: helperText }} />{' '}
        </FormHelperText>
      )}
    </FormControl>
  ),
);

SelectBase.displayName = 'SelectBase';

export type { SelectBaseProps, SelectOption };

export default SelectBase;
